import { TOMBSTONE_TTL_DAYS } from '@nedelnik/shared';
import { useEffect, useState } from 'react';

import { ChevronLeft } from '../icons.js';

import { trashEntries } from './trash.js';

import type { Task } from '@nedelnik/shared';

/**
 * Корзина: удалённые задачи, которые ещё можно вернуть (3.18.6).
 *
 * Механизм был здесь с самого начала и не был виден: удаление ставит
 * надгробие, а не стирает строку (раздел 3.4 архитектуры), — иначе устройство,
 * бывшее оффлайн, не отличило бы «задачу удалили» от «задачу я ещё не
 * получил». Тридцать дней взяты оттуда же: столько надгробие живёт до уборки.
 * Экран не добавляет обратимости, он делает её доступной.
 *
 * Срок написан на экране, а не подразумевается: обещание «вернём» без срока
 * человек достраивает сам, обычно щедрее, чем на самом деле.
 *
 * Стирание насовсем — второе действие строки, и оно в два шага. Сперва его
 * здесь не было: рассуждение было такое, что оно даёт только минус тридцать
 * дней к обратимости, а уборка придёт и сама. Рассуждение неверное — корзина
 * не только хранилище, но и список, на который смотрят: в неё попадает и то,
 * чему в ней делать нечего (проба, случайно записанное), и десяток таких строк
 * прячет за собой то единственное, ради чего сюда заходят. Возврата у шага
 * нет, поэтому подтверждение обязательно, а окончательность сказана прямо в
 * подписи второго шага, а не спрятана в сноску.
 */
export function TrashView({
  tasks,
  now,
  onRestore,
  onPurge,
  onBack,
  backLabel,
}: {
  /** Только надгробия в сроке: отбор делает `trashedOf` в состоянии. */
  tasks: readonly Task[];
  now: Date;
  onRestore: (task: Task) => void;
  onPurge: (task: Task) => void;
  onBack: () => void;
  backLabel: string;
}): React.ReactElement {
  const entries = trashEntries(tasks, now);

  return (
    <section className="screen">
      <header className="screen__head">
        {/*
          Имя начинается с «Назад», а видимая подпись входит в него целиком:
          иначе в озвучке это просто «Сентябрь, кнопка» — слово, которое ничего
          не обещает. Требование «видимый текст — часть имени» при этом
          соблюдено.
        */}
        <button type="button" className="link" aria-label={`Назад: ${backLabel}`} onClick={onBack}>
          <ChevronLeft />
          {backLabel}
        </button>
        <h1>Удалённые</h1>
        <p className="head__sub">
          {TOMBSTONE_TTL_DAYS} дней их можно вернуть, потом они стираются насовсем
        </p>
      </header>

      {entries.length === 0 ? (
        <div className="empty">
          <p>Удалённых задач нет.</p>
          <p>
            Удалённая задача попадает сюда и лежит {TOMBSTONE_TTL_DAYS} дней — всё это время её
            можно вернуть на прежнее место.
          </p>
        </div>
      ) : (
        <ul className="list__rows" aria-label="Удалённые задачи">
          {entries.map(({ task, when, keep }) => (
            // Строка корзины устроена как строка списка, но карточка отсюда не
            // открывается: у удалённой задачи она была бы формой, правки в
            // которой никуда не поедут.
            <li key={task.id} className="row trash__row">
              <span className="row__body">
                <span className="row__head">
                  <span className="trash__title">{task.title}</span>
                </span>
                <span className="row__marks">
                  <span className="tag">{when}</span>
                  <span className="tag">{keep}</span>
                </span>
              </span>

              {/*
                Имена кнопок — с названием задачи: в озвучке подряд идут
                одинаковые «Вернуть», и без названия непонятно, какую из них
                нажимаешь.
              */}
              <span className="trash__actions">
                <button
                  type="button"
                  aria-label={`Вернуть задачу: ${task.title}`}
                  onClick={() => onRestore(task)}
                >
                  Вернуть
                </button>
                <PurgeButton task={task} onPurge={onPurge} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Стирание насовсем — в два шага, как удаление в карточке.
 *
 * Здесь второй шаг обязателен вдвойне: у удаления есть корзина, а у корзины
 * следующей корзины нет. Взвод снимается сам через четыре секунды — иначе
 * кнопка, оставленная взведённой, дожидается случайного нажатия и стирает не
 * то, о чём думали.
 *
 * Подпись второго шага говорит «насовсем», а не «точно?»: в этот момент
 * человек решает не «стирать ли», а «правда ли это без возврата».
 */
function PurgeButton({
  task,
  onPurge,
}: {
  task: Task;
  onPurge: (task: Task) => void;
}): React.ReactElement {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), 4_000);
    return () => clearTimeout(timer);
  }, [armed]);

  const label = armed ? `Стереть насовсем: ${task.title}` : `Стереть задачу: ${task.title}`;

  return (
    <button
      type="button"
      className="danger"
      aria-label={label}
      onClick={() => {
        if (armed) onPurge(task);
        else setArmed(true);
      }}
    >
      {armed ? 'Насовсем?' : 'Стереть'}
    </button>
  );
}
